package at.theatervogelweide.admin.web.screens.acts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.AdminError
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.ErrorCode
import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.ImageCategory
import at.theatervogelweide.admin.contract.ImageDetails
import at.theatervogelweide.admin.contract.acts.ActDraft
import at.theatervogelweide.admin.contract.acts.ActEditor
import at.theatervogelweide.admin.contract.acts.ActService
import at.theatervogelweide.admin.contract.acts.CastPerson
import at.theatervogelweide.admin.web.AdminApi
import at.theatervogelweide.admin.web.Route
import at.theatervogelweide.admin.web.components.AdminButton
import at.theatervogelweide.admin.web.components.AdminConfirmDialog
import at.theatervogelweide.admin.web.components.AdminConflictCallout
import at.theatervogelweide.admin.web.components.AdminPageBody
import at.theatervogelweide.admin.web.components.AdminPageHeader
import at.theatervogelweide.admin.web.components.AdminSectionIndex
import at.theatervogelweide.admin.web.components.Bem
import at.theatervogelweide.admin.web.components.ButtonVariant
import at.theatervogelweide.admin.web.components.EditableImages
import at.theatervogelweide.admin.web.components.IndexedSection
import at.theatervogelweide.admin.web.components.PublicSiteLink
import at.theatervogelweide.admin.web.components.ReportUnsavedChanges
import at.theatervogelweide.admin.web.components.UnsavedChangesGuard
import at.theatervogelweide.admin.web.components.singleImageUuid
import at.theatervogelweide.admin.web.rpcCall
import at.theatervogelweide.domain.TheaterTime
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.rpc.RpcClient
import kotlinx.rpc.withService
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

private val button = Bem("admin-button")
private val alert = Bem("admin-alert")
private val stack = Bem("admin-stack")
private val recordEditor = Bem("admin-record-editor")
private val pageHeader = Bem("admin-page-header")

/** The editor of one act, or of a new one while [loaded] is `null`. */
internal class ActEditorState(
    loaded: ActEditor?,
    val mainImage: EditableImages,
    val images: EditableImages,
    /** What a new act starts with (ActService.newDraft); unused for a saved one. */
    newDraft: ActDraft = ActDraft(),
) {
    var loaded: ActEditor? by mutableStateOf(loaded)
    val original: ActDraft = loaded?.draft ?: newDraft.copy(year = TheaterTime.fromUtc(now()).year)
    private val initialForm = ActForm.of(original)

    var form: ActForm by mutableStateOf(initialForm)
    var fieldErrors: List<FieldError> by mutableStateOf(emptyList())
    var error: String? by mutableStateOf(null)
    var conflict: Boolean by mutableStateOf(false)
    var saving: Boolean by mutableStateOf(false)
    var reloading: Boolean by mutableStateOf(false)
    var deleting: Boolean by mutableStateOf(false)

    /** The cast row whose name field takes the focus once it is in the page. */
    var focusCastRow: Int? by mutableStateOf(null)

    /** Nothing may change while the server's answer is about to replace this editor. */
    val busy: Boolean get() = saving || reloading || deleting

    val isNew: Boolean get() = loaded == null

    val hasChanges: Boolean
        get() = form != initialForm || mainImage.singleImageUuid != original.mainImageUuid ||
            images.uuids != original.imageUuids || imageDetails != original.imageDetails || mainImage.uploading || images.uploading

    /** The details of the title image and the other images, to compare with the loaded ones. */
    val imageDetails get() = mainImage.currentDetails + images.currentDetails

    fun errorFor(field: String): String? = fieldErrors.firstOrNull { it.field == field }?.message

    fun showFailure(failure: AdminError) {
        conflict = failure.code == ErrorCode.CONFLICT
        fieldErrors = failure.fieldErrors
        error = when {
            conflict -> null
            failure.fieldErrors.isNotEmpty() -> failure.message
            failure.code == ErrorCode.NOT_FOUND -> "Dieses Stück gibt es nicht mehr."
            else -> failure.message
        }
    }

    fun dispose() {
        mainImage.dispose()
        images.dispose()
    }
}

/**
 * The editor of an act (#22) with its sections Allgemein, Termine & Karten, Spielstätte & Eintritt (#103), Besetzung
 * and Bilder beside a jump list.
 * [actId] comes from the address, `null` for a new act; [onNavigate] changes the address, replacing the entry with
 * `replace`.
 */
@Composable
fun ActEditorScreen(
    rpc: RpcClient,
    api: AdminApi,
    guard: UnsavedChangesGuard,
    actId: Long?,
    onNavigate: (route: Route, replace: Boolean) -> Unit,
) {
    val service = remember(rpc) { rpc.withService<ActService>() }
    val scope = rememberCoroutineScope()

    var editor by remember { mutableStateOf<ActEditorState?>(null) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var people by remember { mutableStateOf<List<CastPerson>?>(null) }
    var confirmDelete by remember { mutableStateOf(false) }

    fun newEditor(loaded: ActEditor?, newDraft: ActDraft = ActDraft()): ActEditorState {
        editor?.dispose()
        return ActEditorState(
            loaded,
            newImages(scope, api, listOfNotNull(loaded?.draft?.mainImageUuid), loaded?.draft?.imageDetails.orEmpty()),
            newImages(scope, api, loaded?.draft?.imageUuids.orEmpty(), loaded?.draft?.imageDetails.orEmpty()),
            newDraft,
        )
    }

    /** Loads [id] in place of [replacing]; the answer is dropped if another editor opened meanwhile. */
    suspend fun loadEditor(id: Long, replacing: ActEditorState? = editor) {
        val result = rpcCall { service.load(id) }
        if (editor !== replacing) {
            return
        }
        when (result) {
            is AdminResult.Success -> {
                editor = newEditor(result.value)
                loadError = null
            }

            is AdminResult.Failure -> {
                editor?.dispose()
                editor = null
                loadError = if (result.error.code == ErrorCode.NOT_FOUND) "Dieses Stück gibt es nicht mehr." else result.error.message
            }
        }
    }

    LaunchedEffect(service, actId) {
        when {
            actId == null -> if (editor?.isNew != true) {
                val replacing = editor
                when (val result = rpcCall { service.newDraft() }) {
                    is AdminResult.Success -> if (editor === replacing) {
                        loadError = null
                        editor = newEditor(null, result.value)
                    }

                    is AdminResult.Failure -> if (editor === replacing) {
                        editor?.dispose()
                        editor = null
                        loadError = result.error.message
                    }
                }
            }

            editor?.loaded?.id != actId -> loadEditor(actId)
        }
    }

    LaunchedEffect(service) {
        (rpcCall { service.people() } as? AdminResult.Success)?.let { people = it.value }
    }

    DisposableEffect(Unit) { onDispose { editor?.dispose() } }

    val state = editor
    ReportUnsavedChanges(guard, state?.hasChanges == true)

    fun toList(replace: Boolean = false) = onNavigate(Route.Acts(), replace)

    fun save() {
        val current = editor ?: return
        val (draft, errors) = current.form.toDraft(
            current.mainImage.singleImageUuid,
            current.images.uuids,
            current.mainImage.detailsToSave.filterKeys { it == current.mainImage.singleImageUuid } + current.images.detailsToSave,
        )
        current.fieldErrors = errors
        current.conflict = false
        current.error = if (errors.isEmpty()) null else AdminError.validation(errors).message
        if (draft == null || current.saving) {
            return
        }

        current.saving = true
        scope.launch {
            val loaded = current.loaded
            val result = rpcCall { if (loaded == null) service.create(draft) else service.save(loaded.versionedId, draft) }
            current.saving = false
            if (editor !== current) {
                return@launch
            }
            when (result) {
                is AdminResult.Success -> {
                    editor = newEditor(result.value)
                    if (loaded == null) {
                        onNavigate(Route.Acts(result.value.id), true)
                    }
                }

                is AdminResult.Failure -> current.showFailure(result.error)
            }
        }
    }

    fun discard() {
        val current = editor ?: return
        val loaded = current.loaded
        if (loaded == null) {
            current.dispose()
            editor = null
            toList(replace = true)
        } else {
            editor = newEditor(loaded)
        }
    }

    fun reloadAfterConflict() {
        val current = editor ?: return
        val id = current.loaded?.id ?: return
        if (current.reloading) {
            return
        }
        current.reloading = true
        scope.launch {
            try {
                loadEditor(id, replacing = current)
            } finally {
                current.reloading = false
            }
        }
    }

    fun delete() {
        confirmDelete = false
        val current = editor ?: return
        val loaded = current.loaded ?: return
        current.deleting = true
        scope.launch {
            val result = rpcCall { service.delete(loaded.versionedId) }
            current.deleting = false
            if (editor !== current) {
                return@launch
            }
            when (result) {
                is AdminResult.Success -> {
                    current.dispose()
                    editor = null
                    toList(replace = true)
                }

                is AdminResult.Failure -> current.showFailure(result.error)
            }
        }
    }

    val title = state?.loaded?.draft?.title ?: if (actId == null) "Neues Stück" else "Stück"
    AdminPageHeader(
        title = title,
        unsavedChanges = state?.hasChanges == true,
        breadcrumb = {
            A(Route.Acts.PATH, {
                classes(pageHeader.element("breadcrumb-link").className)
                onClick { event ->
                    if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
                        event.preventDefault()
                        guard.proceed { toList() }
                    }
                }
            }) { Text("Stücke") }
            state?.loaded?.let { Text(" / ${it.draft.year}") }
        },
    ) {
        if (state != null) {
            state.loaded?.let { loaded ->
                PublicSiteLink("/archive/${loaded.id}", button.block, button.modifier("link")) { Text("Auf Website ansehen ↗") }
            }
            AdminButton(disabled = state.busy || (!state.hasChanges && !state.isNew), onClick = ::discard) { Text("Verwerfen") }
            AdminButton(
                variant = ButtonVariant.PRIMARY,
                type = ButtonType.Submit,
                disabled = state.busy || state.mainImage.uploading || state.images.uploading || (!state.hasChanges && !state.isNew),
                form = FORM_ID,
            ) { Text(if (state.saving) "Speichert …" else "Speichern") }
        }
    }

    AdminPageBody {
        when {
            state != null -> Div({ classes(recordEditor.block) }) {
                Form(attrs = {
                    id(FORM_ID)
                    classes(stack.block, stack.modifier("editor"))
                    attr("novalidate", "")
                    onSubmit { event ->
                        event.preventDefault()
                        save()
                    }
                    onKeyDown { event ->
                        // the old role editor's shortcut for a new role
                        if (event.ctrlKey && event.altKey && event.key.lowercase() == "a" && !state.busy) {
                            event.preventDefault()
                            state.form = state.form.addRole(CastGroup.CAST)
                            state.focusCastRow = state.form.cast.last().key
                        }
                    }
                }) {
                    if (state.conflict || state.error != null) {
                        Div({ classes(recordEditor.element("messages").className) }) {
                            if (state.conflict) {
                                AdminConflictCallout(onReload = ::reloadAfterConflict)
                            }
                            state.error?.let {
                                P({
                                    classes(alert.block)
                                    attr("role", "alert")
                                }) { Text(it) }
                            }
                        }
                    }

                    GeneralSection(state)
                    PerformancesSection(state)
                    VenueSection(state)
                    CastSection(state, people)
                    ImagesSection(state, api)
                }

                AdminSectionIndex(
                    sections = listOf(
                        IndexedSection(SECTION_GENERAL, "Allgemein"),
                        IndexedSection(SECTION_PERFORMANCES, "Termine & Karten", state.form.performances.size),
                        IndexedSection(SECTION_VENUE, "Spielstätte & Eintritt"),
                        IndexedSection(SECTION_CAST, "Besetzung", state.form.cast.size + state.form.crew.size),
                        IndexedSection(SECTION_IMAGES, "Bilder", state.mainImage.images.size.coerceAtMost(1) + state.images.images.size),
                    ),
                    footer = if (state.isNew) null else {
                        {
                            AdminButton(variant = ButtonVariant.DANGER, block = true, disabled = state.busy, onClick = { confirmDelete = true }) {
                                Text("Stück löschen")
                            }
                        }
                    },
                )
            }

            loadError != null -> P({
                classes(alert.block)
                attr("role", "alert")
            }) { Text(loadError!!) }

            else -> P { Text("Lade …") }
        }
    }

    AdminConfirmDialog(
        open = confirmDelete,
        id = "stueck-loeschen",
        title = "Stück löschen?",
        message = "„${state?.loaded?.draft?.title.orEmpty()}“ verschwindet mit Terminen, Besetzung und Bildern aus dem " +
            "Archiv und von der Kartenseite. Das lässt sich nicht rückgängig machen.",
        confirmLabel = "Löschen",
        cancelLabel = "Behalten",
        onConfirm = ::delete,
        onCancel = { confirmDelete = false },
        danger = true,
    )
}

private const val FORM_ID = "stueck"
internal const val SECTION_GENERAL = "allgemein"
internal const val SECTION_PERFORMANCES = "termine"
internal const val SECTION_VENUE = "spielstaette"
internal const val SECTION_CAST = "besetzung"
internal const val SECTION_IMAGES = "bilder"

private fun newImages(scope: CoroutineScope, api: AdminApi, uuids: List<String>, details: Map<String, ImageDetails>) =
    EditableImages(scope, { file -> api.uploadImage(ImageCategory.ACTS, file) }, uuids, details = details)
